import { z } from 'zod';
import { emailSchema, passwordSchema, ROLE_LABELS } from './auth';
import { paginatedSchema, paginationQuerySchema } from './pagination';

export const USER_STATUSES = ['INVITED', 'ACTIVE', 'DISABLED'] as const;
export type UserStatusName = (typeof USER_STATUSES)[number];
export const USER_STATUS_LABELS: Record<UserStatusName, string> = {
  INVITED: 'Convite pendente',
  ACTIVE: 'Ativo',
  DISABLED: 'Desativado',
};

const ROLE_KEYS = Object.keys(ROLE_LABELS) as [keyof typeof ROLE_LABELS, ...(keyof typeof ROLE_LABELS)[]];

/** Usuário de uma empresa na administração (lista e detalhe). */
export const orgUserSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string(),
  status: z.enum(USER_STATUSES),
  role: z.object({ id: z.string().uuid(), key: z.enum(ROLE_KEYS), name: z.string() }),
  departments: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  businessUnits: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  lastLoginAt: z.string().nullable(),
  /** Verificação em duas etapas ativa. */
  twoFactorEnabled: z.boolean(),
  /** Quando foi cadastrado (convidado). */
  createdAt: z.string(),
  /** Último convite enviado (só para quem ainda não aceitou). */
  invitation: z.object({ sentAt: z.string(), expiresAt: z.string(), expired: z.boolean() }).nullable(),
});
export type OrgUser = z.infer<typeof orgUserSchema>;
export const orgUserListSchema = paginatedSchema(orgUserSchema);

export const userListQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(100, 'Busca longa demais.').optional(),
  roleKey: z.enum(ROLE_KEYS).optional(),
  status: z.enum(USER_STATUSES).optional(),
});
export type UserListQuery = z.input<typeof userListQuerySchema>;

const idList = (label: string) =>
  z
    .array(z.string().uuid(`${label} inválida.`))
    .max(50, `No máximo 50 ${label.toLowerCase()}s.`)
    .default([])
    .transform((ids) => [...new Set(ids)]);

/** Dados de acesso de um usuário: nome, perfil, áreas e unidades. */
export const userAccessInputSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome.').max(120, 'Nome longo demais.'),
  roleId: z.string().uuid('Escolha um perfil.'),
  departmentIds: idList('Área'),
  businessUnitIds: idList('Unidade'),
});
export type UserAccessInput = z.input<typeof userAccessInputSchema>;

/** Convite: os dados de acesso + o e-mail da pessoa. */
export const inviteUserInputSchema = userAccessInputSchema.extend({ email: emailSchema });
export type InviteUserInput = z.input<typeof inviteUserInputSchema>;

/** Opções do formulário de usuário: perfis da empresa e áreas/unidades ativas. */
export const userFormOptionsSchema = z.object({
  roles: z.array(z.object({ id: z.string().uuid(), key: z.enum(ROLE_KEYS), name: z.string() })),
  departments: z.array(
    z.object({ id: z.string().uuid(), name: z.string(), parentId: z.string().uuid().nullable() }),
  ),
  businessUnits: z.array(z.object({ id: z.string().uuid(), name: z.string(), code: z.string().nullable() })),
});
export type UserFormOptions = z.infer<typeof userFormOptionsSchema>;

/** Aceitar convite: o token vem do link (fragmento #token=), a senha segue as regras da plataforma. */
export const invitationTokenSchema = z.string().trim().min(20, 'Link inválido.').max(200, 'Link inválido.');
export const invitationCheckSchema = z.object({ token: invitationTokenSchema });
export const invitationInfoSchema = z.object({
  name: z.string(),
  email: z.string(),
  organizationName: z.string(),
});
export type InvitationInfo = z.infer<typeof invitationInfoSchema>;
export const acceptInvitationSchema = z.object({ token: invitationTokenSchema, password: passwordSchema });

/** Espera do app antes de permitir reenviar o convite (o servidor aceita um pouco antes). */
export const INVITATION_RESEND_SECONDS = 60;
